import { React, useState, useEffect, useRef } from "react";
import MiscUploadPDFView from "../misc_components/MiscUploadPDFView";
import Footer from "../Footer";
import NavBar from "../NavBar";
import MiscPageTitle from "../misc_components/MiscPageTitle";
import MiscPageMidSection from "../misc_components/MiscPageMidSection";
import HomeScreenHowToUse from "../home_screen/HomeScreenHowToUse";
import MiscWhyUse from "../misc_components/MiscWhyUse";
import MiscPageLookingFor from "../misc_components/MiscPageLookingFor";
import LoadingBar from "react-top-loading-bar";
import { useLocation, ScrollRestoration } from "react-router-dom";
import { PageContext } from "../../App";
import PDFMetadataEditorImageListScreen from "./PDFMetadataEditorImageListScreen";
import { BASE_URL } from "../Config";
import MiscFAQ from "../misc_components/MiscFAQ";
import MiscRecommendedPages from "../misc_components/MiscRecommendedPages";

function PDFMetadataEditorMain() {
  //location
  const location = useLocation();
  // Use optional chaining or a guard in case state is undefined
  const { title } = location.state || { title: "PDF Metadata Editor" };

  //selected files
  const [selectedFiles, setSelectedFiles] = useState([]);
  //show edit screen
  const [showEditFileScreen, setShowEditFileScreen] = useState(false);
  //start processing
  const [startProcessingFiles, setStartProcessingFiles] = useState(false);

  //reference loading bar
  const loadingBarRef = useRef(null);

  //looking for object
  //requires 3 data objects
  //to show keywarded navigation
  const lookingForObject = {
    data1: { link: "/pdf_metadata_editor", title: "Edit PDF Metadata" },
    data2: { link: "/pdf_metadata_editor", title: "PDF Editor Online" },
    data3: { link: "/pdf_metadata_editor", title: "PDF Properties Editor" },
  };

  //use effect to handle the submit button click
  useEffect(() => {
    //check
    if (startProcessingFiles === true) {
      //show file edit screen
      setShowEditFileScreen(true);

      console.log(
        "Start file processing. Number of files: ",
        selectedFiles.length
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startProcessingFiles]);

  return (
    <PageContext.Provider
      value={{
        selectedFiles,
        setSelectedFiles,
        startProcessingFiles,
        setStartProcessingFiles,
        setShowEditFileScreen,
      }}
    >
      {/* Scroll to top */}
      <ScrollRestoration />
      {/* Loading Bar */}
      <LoadingBar ref={loadingBarRef} />

      {/*Add title and metadata */}
      <title>{title + " Offline"}</title>
      <meta name="robots" content="index, follow"></meta>
      <link
        rel="canonical"
        href={`${BASE_URL}${lookingForObject.data1.link}`}
      />
      <meta
        name="description"
        content="Edit PDF metadata online easily. Change title, author, keywords, and properties quickly with our fast and secure PDF metadata editor."
      ></meta>
      <meta
        name="keywords"
        content="pdf metadata editor, edit pdf metadata, pdf properties editor, change pdf metadata, pdf title editor, pdf author editor, edit pdf properties, online pdf metadata editor, pdf keyword editor, pdf info editor, metadata pdf tool, pdf document properties, pdf metadata changer, pdf editor online, pdf management tool"
      ></meta>

      {/* page content */}
      {showEditFileScreen ? (
        <PDFMetadataEditorImageListScreen
          title={title}
        ></PDFMetadataEditorImageListScreen>
      ) : (
        <div>
          {/* Nav Bar */}
          <NavBar />
          {/* Title */}
          <MiscPageTitle title={title} online={false} />
          {/* Upload PDF View */}
          <MiscUploadPDFView
            selectMultiple={true}
            filetype={{ "application/pdf": [".pdf"] }}
          />
          {/* Mid Section */}
          <MiscPageMidSection description="Our PDF Metadata Editor website provides a secure, powerful, and easy-to-use solution to edit and manage PDF metadata both offline and online. Built for professionals, students, and businesses, this advanced tool allows users to edit PDF title, author, subject, keywords, producer, and other document properties with precision and speed. With offline functionality, all metadata editing is performed directly on your device, ensuring complete privacy and data protection without uploading files to external servers. When internet access is available, users can also edit PDF online using a smooth, browser-based interface for quick updates and real-time document optimization. Properly edited PDF metadata improves document organization, accessibility, and search engine indexing, helping files rank better in search results. As a versatile PDF editor online, the platform supports essential document workflows and integrates seamlessly with other PDF tools such as compression, resizing, and format optimization. Using advanced processing technology, the editor preserves original formatting and content quality while updating metadata instantly. Compatible with desktop and mobile devices, this PDF metadata editor helps improve SEO for PDF files, enhance document discoverability, and maintain professional standards. If you are looking for the best PDF metadata editor online, offline PDF metadata editor, PDF editor online, or a secure way to edit PDF online, this website delivers a complete, reliable, and privacy-focused PDF editing experience." />
          {/* How to use */}
          <HomeScreenHowToUse
            steps={{
              step1: "Select",
              step2: "Offline",
              step3: "Edit",
              step4: "Download",
            }}
          />
          {/* Why use section */}
          <MiscWhyUse
            title="PDF Metadata Editor"
            why_use={[
              {
                title: "Improve PDF Search Visibility (SEO)",
                description:
                  "Our PDF metadata editor allows you to edit PDF title, author, subject, and keywords, helping search engines index your documents correctly and improving rankings in search results.",
              },
              {
                title: "Better Document Organization",
                description:
                  "Editing PDF metadata helps organize large collections of files. With a PDF metadata editor online, you can manage document properties efficiently and find files faster.",
              },
              {
                title: "Edit PDF Online with Ease",
                description:
                  "Using an edit PDF online tool, users can quickly update metadata without installing heavy software, saving time and effort.",
              },

              {
                title: "Enhance Professional Appearance",
                description:
                  "Accurate metadata makes PDFs look more professional. A reliable PDF editor online ensures your documents display correct author names, titles, and descriptions.",
              },
              {
                title: "Protect Privacy and Sensitive Information",
                description:
                  "PDF files often contain hidden metadata. A PDF metadata editor offline helps remove or update sensitive details to maintain privacy and data security.",
              },
              {
                title: "Improve Accessibility and Compatibility",
                description:
                  "Well-structured metadata improves accessibility for screen readers and ensures better compatibility across devices and platforms using a trusted PDF metadata editor.",
              },
              {
                title: "Optimize PDFs for Sharing and Publishing",
                description:
                  "When sharing or publishing documents online, optimized metadata increases discoverability. A PDF metadata editor online helps your PDFs perform better on websites and content platforms.",
              },
              {
                title: "Support SEO-Friendly PDF Content",
                description:
                  "Search engines rely on metadata to understand PDF content. Using a PDF metadata editor ensures your PDFs are optimized with relevant keywords for better SEO performance.",
              },
              {
                title: "Work Offline and Online Anytime",
                description:
                  "This offline and online PDF metadata editor gives flexibility to edit documents securely without internet access or quickly edit PDF online when connected.",
              },
            ]}
          />
          {/* FAQ */}
          <MiscFAQ
            faq_list={[
              {
                title: "What is a PDF metadata editor?",
                description:
                  "A PDF metadata editor is a tool that lets you view or modify document properties such as title, author, subject, and keywords. Editing metadata helps organize PDF files, improve document identification, and manage digital documents."
              },
              {
                title: "How can I edit PDF metadata online for free?",
                description:
                  "Open a PDF metadata editor, select your PDF file, modify the supported document properties, and save or download the updated PDF. Available metadata fields depend on the tool and the document."
              },
              {
                title: "Can I edit PDF metadata offline?",
                description:
                  "Yes, if the PDF metadata editor supports offline processing. A browser-based tool that works locally can modify PDF properties on your device after the website and required resources have loaded."
              },
              {
                title: "Does ShivaPDF edit PDF metadata locally in my browser?",
                description:
                  "Local processing means your PDF metadata is edited directly in your browser instead of being uploaded to a remote server. Check that the PDF Metadata Editor on ShivaPDF.com supports local processing before using it for confidential documents."
              },
              {
                title: "Can I edit PDF metadata without uploading my file?",
                description:
                  "Yes, if the metadata editor processes files entirely locally in your browser. Your document can then remain on your device instead of being sent to a server. Verify local processing support before editing sensitive PDFs."
              },
              {
                title: "Is it safe to edit PDF metadata online?",
                description:
                  "Choose a trusted PDF metadata editor and review its file-processing practices. For confidential documents, consider a tool that processes files locally without uploading them to a remote server."
              },
              {
                title: "Which PDF metadata fields can I edit?",
                description:
                  "Depending on the editor, you may be able to modify properties such as title, author, subject, and keywords. Some tools may also support additional document properties, while others offer only a limited set of fields."
              },
              {
                title: "How do I change the title of a PDF?",
                description:
                  "Open your PDF in a metadata editor, locate the document title field, enter the new title, and save the updated file. The title is a document property and may differ from the PDF's filename."
              },
              {
                title: "How can I change the author name in a PDF?",
                description:
                  "Use a PDF metadata editor that supports the author field. Enter the preferred author name and save the document to update its displayed metadata, if the tool supports this property."
              },
              {
                title: "Can I add keywords to PDF metadata?",
                description:
                  "If keyword editing is supported, you can add relevant terms to a PDF's metadata to help describe and organize the document. Use accurate keywords that reflect the document's actual content."
              },
              {
                title: "Can I change the subject of a PDF document?",
                description:
                  "A PDF metadata editor with subject editing lets you update the subject property to describe the document's purpose or topic. Save the updated PDF to apply the change."
              },
              {
                title: "What is the difference between PDF metadata and a filename?",
                description:
                  "A filename is the name used to identify a file in your storage system, while metadata contains properties stored inside the document, such as its title, author, and subject. Changing one does not necessarily change the other."
              },
              {
                title: "Does editing PDF metadata change the document's content?",
                description:
                  "Editing metadata generally changes document properties rather than the visible text, images, or page layout. However, the saved PDF may be rewritten during processing, so review the output to confirm that its content remains intact."
              },
              {
                title: "Can I remove metadata from a PDF?",
                description:
                  "Some PDF metadata editors let you clear selected properties, while others may offer options to remove metadata more broadly. Check which fields are supported and remember that metadata removal may not remove all hidden information or annotations."
              },
              {
                title: "Can I view PDF metadata without editing it?",
                description:
                  "Yes, PDF readers and metadata tools can display document properties such as title, author, subject, and creation information. Whether a browser-based editor provides a read-only view depends on its features."
              },
              {
                title: "Can I edit metadata in a scanned PDF?",
                description:
                  "You can often edit the document properties of a scanned PDF without changing the scanned page images. Support depends on the PDF structure and the metadata editor's capabilities."
              },
              {
                title: "Can I edit metadata in a password-protected PDF?",
                description:
                  "A password-protected PDF may require its password before its metadata can be modified. Whether editing is allowed depends on the document's security settings and the tool's capabilities."
              },
              {
                title: "Will editing metadata affect PDF quality?",
                description:
                  "Changing metadata should not normally reduce the visual quality of PDF pages. However, the way the file is saved can vary by tool, so check the updated PDF after editing."
              },
              {
                title: "Can I edit PDF metadata on my mobile phone?",
                description:
                  "You can use a mobile-compatible PDF metadata editor on an Android phone, iPhone, or tablet. Available fields and performance depend on your browser, device, and the tool's supported features."
              },
              {
                title: "Can I edit PDF metadata on Windows and Mac?",
                description:
                  "A compatible browser-based metadata editor can work on Windows and macOS without requiring separate desktop software. If local processing is supported, the document can be modified directly in the browser."
              },
              {
                title: "Do I need to install software to edit PDF metadata?",
                description:
                  "A browser-based PDF metadata editor can let you modify document properties without installing a separate application. Depending on its offline capabilities, an internet connection may still be required to load the website."
              },
              {
                title: "Why are my PDF metadata changes not saving?",
                description:
                  "Make sure you have applied the changes and downloaded or saved the updated PDF. If the metadata remains unchanged, try processing the original file again and check the saved document's properties in a compatible PDF reader."
              },
              {
                title: "Can editing PDF metadata improve SEO?",
                description:
                  "Adding an accurate title and relevant metadata can improve document organization and may help search engines understand a PDF's subject. Metadata alone does not guarantee better rankings; useful content, accessibility, and how the PDF is published also matter."
              },
              {
                title: "Is the PDF Metadata Editor on ShivaPDF free to use?",
                description:
                  "Visit the PDF Metadata Editor page on ShivaPDF.com to check current availability, supported metadata fields, file-size limits, and usage restrictions. Features depend on the tool's current implementation."
              }
            ]} />
          {/* Looking for section */}
          <MiscPageLookingFor data={lookingForObject} />
          {/* Recommended Tools */}
          <MiscRecommendedPages />
          {/* Footer */}
          <Footer />
        </div>
      )}
    </PageContext.Provider>
  );
}

export default PDFMetadataEditorMain;
